<script lang="ts">
	/**
	 * The headline figures (Design §13) — what the store took, what went back out, and what it kept.
	 *
	 * **Everything is per currency and nothing is ever summed across them.** Minor units in two
	 * currencies are different units, and a "total" across them is the one shape on this page that
	 * would be wrong in a way nobody could see. So a store selling in three currencies gets three
	 * blocks, and the caption says why there is no fourth.
	 *
	 * **Every figure is against the same window immediately before it.** A number with no
	 * comparison is not information: "€41,200" is a good month or a catastrophe depending entirely
	 * on what last month was, and a report that makes the reader remember is a report nobody checks
	 * twice.
	 *
	 * The per-kind table under the blocks is not decoration. `net` is a single number arrived at
	 * from five different movements, and the operator's next question after reading it is always
	 * *which* — so the arithmetic that produced it is on the same screen rather than a click away.
	 */

	import { Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import type { ReportCurrencyTotals, ReportEventKind, ReportSummary } from '../../../AppTypes';

	export let summary: ReportSummary | null = null;
	export let loading: boolean = false;
	export let error: string = '';

	const KIND_COPY: Record<ReportEventKind, string> = {
		sale: 'Sales',
		renewal: 'Renewals',
		adjustment: 'Adjustments',
		refund: 'Refunds',
		chargeback: 'Chargebacks'
	};

	function fnMoney(amount: number, currency: string): string {
		// The fallback reports raw minor units and says so: a client that quietly divides by 100
		// renders ¥1,200 as ¥12 and looks entirely plausible doing it.
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}

	/**
	 * A change against the previous period, as words rather than as a colour alone.
	 *
	 * "no comparison" rather than an infinity or a 100% when the previous window was zero: a store
	 * whose first sale landed this month did not grow by any percentage, and printing one invites
	 * it into a slide.
	 */
	function fnDelta(current: number, previous: number): string {
		if (previous === 0) return current === 0 ? 'no change' : 'no comparison';
		const change = ((current - previous) / previous) * 100;
		const rounded = Math.abs(change) < 10 ? change.toFixed(1) : Math.round(change).toString();
		return `${change >= 0 ? '+' : ''}${rounded}% vs previous`;
	}

	/** The previous window's row for one currency, or a zeroed one — never `undefined`. */
	function fnPrevious(currency: string, previous: ReportCurrencyTotals[]): ReportCurrencyTotals {
		return (
			previous.find(entry => entry.currency === currency) ?? {
				currency,
				gross: 0,
				refunded: 0,
				chargebacks: 0,
				tax: 0,
				net: 0,
				netExcludingTax: 0,
				movements: 0
			}
		);
	}

	/**
	 * Refunds over gross, in the same currency and window.
	 *
	 * A currency with nothing coming in reports no rate at all rather than dividing by zero — the
	 * honest answer to "what fraction of nothing went back" is that there is not one.
	 */
	function fnRefundRate(totals: ReportCurrencyTotals): string {
		if (totals.gross <= 0) return '—';
		return `${((totals.refunded / totals.gross) * 100).toFixed(1)}%`;
	}
</script>

<Panel title="Summary" faIcon="scale-balanced">
	<svelte:fragment slot="title">
		<span class="text-xs text-default">
			Per currency, and never summed across them — minor units in two currencies are different
			units.
		</span>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="The summary could not be loaded." description={error} />
	{:else if summary === null}
		<EmptyState variant="loading" />
	{:else if summary.totals.length === 0}
		<EmptyState
			variant="empty"
			title="No money moved in this range."
			description="Widen the range, or clear a filter that may be narrowing it to nothing."
		/>
	{:else}
		<div class="grid grid-cols-1 xl:grid-cols-2 gap-3" class:opacity-50={loading}>
			{#each summary.totals as totals (totals.currency)}
				{@const previous = fnPrevious(totals.currency, summary.previous)}
				<div class="widget bg-depth">
					<div class="card-label">
						<i class="fa-solid fa-coins text-[11px] mr-1 text-default" />
						{totals.currency.toUpperCase()}
					</div>

					<div class="mt-2 flex items-baseline justify-between gap-3">
						<span class="text-sm text-default">Net</span>
						<span class="text-2xl font-semibold tabular-nums">
							{fnMoney(totals.net, totals.currency)}
						</span>
					</div>
					<div class="text-xs text-default text-right">
						{fnDelta(totals.net, previous.net)}
					</div>

					<dl class="mt-3 space-y-1 text-sm">
						<div class="flex items-baseline justify-between gap-3">
							<dt class="text-default">Money in</dt>
							<dd class="tabular-nums">{fnMoney(totals.gross, totals.currency)}</dd>
						</div>
						<div class="flex items-baseline justify-between gap-3">
							<dt class="text-default">Refunded</dt>
							<!-- Negated, so the column reads as the subtraction it is rather than as a
							     second amount taken in — and negated rather than prefixed with a minus,
							     because `formatMoney` already puts a sign before the symbol and the two
							     together would render `-−€266.44`. -->
							<dd class="tabular-nums">
								{totals.refunded === 0 ? '—' : fnMoney(-totals.refunded, totals.currency)}
							</dd>
						</div>
						<div class="flex items-baseline justify-between gap-3">
							<dt class="text-default">Charged back</dt>
							<dd class="tabular-nums">
								{totals.chargebacks === 0
									? '—'
									: fnMoney(-totals.chargebacks, totals.currency)}
							</dd>
						</div>
						<div class="flex items-baseline justify-between gap-3 pt-1 border-t border-widget-color">
							<dt class="text-default">Tax within it</dt>
							<dd class="tabular-nums">{fnMoney(totals.tax, totals.currency)}</dd>
						</div>
						<div class="flex items-baseline justify-between gap-3">
							<dt class="text-default">Net of tax</dt>
							<dd class="tabular-nums font-medium">
								{fnMoney(totals.netExcludingTax, totals.currency)}
							</dd>
						</div>
						<div class="flex items-baseline justify-between gap-3">
							<dt class="text-default">Refund rate</dt>
							<dd class="tabular-nums">{fnRefundRate(totals)}</dd>
						</div>
						<div class="flex items-baseline justify-between gap-3">
							<dt class="text-default">Movements</dt>
							<dd class="tabular-nums">{totals.movements.toLocaleString()}</dd>
						</div>
					</dl>
				</div>
			{/each}
		</div>

		<!-- What the net above is made of. Signed, as the ledger stores it, because a refund row
		     reading as a positive amount in a column headed "Amount" is the reading that gets
		     added rather than subtracted. -->
		<div class="overflow-x-auto mt-4">
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>Movement</TableHeadCell>
					<TableHeadCell>Currency</TableHeadCell>
					<TableHeadCell class="text-right">Amount</TableHeadCell>
					<TableHeadCell class="text-right">Tax</TableHeadCell>
					<TableHeadCell class="text-right">Net of tax</TableHeadCell>
					<TableHeadCell class="text-right">Count</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each summary.byKind as entry (`${entry.kind}:${entry.currency}`)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">{KIND_COPY[entry.kind]}</TableBodyCell>
							<TableBodyCell class="dataTable font-mono text-xs uppercase">
								{entry.currency}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(entry.amount, entry.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(entry.tax, entry.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(entry.net, entry.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{entry.movements.toLocaleString()}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>
	{/if}
</Panel>
